import { AsYouType, Metadata } from 'libphonenumber-js'

const E164_MAX_DIGITS = 15

/**
 * Normalize raw phone input to +E.164 digits (no formatting).
 * Leading 8… (without +) is treated as Russia (+7…), same as existing paste handlers.
 */
export function normalizePhoneDigits(raw) {
  let text = String(raw ?? '').trim()
  if (!text) {
    return ''
  }

  const digitsOnly = text.replace(/\D/g, '')
  if (!digitsOnly) {
    return text.includes('+') ? '+' : ''
  }

  if (!text.includes('+') && digitsOnly.startsWith('8')) {
    return `+7${digitsOnly.slice(1, E164_MAX_DIGITS)}`
  }

  return `+${digitsOnly.slice(0, E164_MAX_DIGITS)}`
}

function getMaxDigitsForCountry(country) {
  if (!country) {
    return E164_MAX_DIGITS
  }

  try {
    const metadata = new Metadata()
    metadata.selectNumberingPlan(country)
    const nationalLengths = metadata.numberingPlan?.possibleLengths()
    const callingCode = metadata.numberingPlan?.callingCode()
    if (!nationalLengths?.length || !callingCode) {
      return E164_MAX_DIGITS
    }

    return Math.min(
      E164_MAX_DIGITS,
      String(callingCode).length + Math.max(...nationalLengths),
    )
  } catch {
    return E164_MAX_DIGITS
  }
}

/**
 * Format phone as-you-type via libphonenumber.
 *
 * Do not stop on isValid()/isPossible() of the current prefix: many countries
 * have gaps (valid → not possible → valid again), e.g. KR +82 105839274.
 * Cap only by E.164 (15) and the country's max possible length from metadata.
 */
export function formatPhoneInput(raw) {
  const normalized = normalizePhoneDigits(raw)
  if (!normalized || normalized === '+') {
    return normalized
  }

  const digits = normalized.slice(1)
  const formatter = new AsYouType()
  formatter.input('+')

  let country
  let accepted = ''

  for (const digit of digits) {
    formatter.input(digit)
    country = formatter.getCountry() || formatter.getNumber()?.country || country
    if (accepted.length + 1 > getMaxDigitsForCountry(country)) {
      break
    }
    accepted += digit
  }

  return new AsYouType().input(`+${accepted}`)
}
